// `useFtuChecklist()` — derives the floating checklist state from the
// authenticated user's onboarding + visited-pages data (US-034, US-056).
//
// Behaviour:
//   - Reads `users.visitedPages` via the `useMe()` query (the user object
//     carries the column transparently because the GET /me handler returns
//     the row verbatim).
//   - Returns 6 checklist items; each ticks as soon as the user has visited
//     the corresponding route prefix at least once.
//   - The auto-tick logic is delegated to the parent shell, which calls
//     `track(prefix)` on every router navigation. We expose `track` so
//     Wave 5a.2's `AppShell` can wire it without re-implementing storage.

import { useCallback, useMemo } from 'react';

import { useLinkedinAccounts, useMe, useTrackVisitedPages } from '@barel/api-client/react';
import { useT } from '@barel/i18n';

import type { FtuChecklistItem } from '@barel/ui';

interface ChecklistDef {
  id: string;
  /** Path prefix that auto-ticks the item once visited. */
  prefix: string;
  /** Path the item navigates to when clicked. */
  to: string;
  /** Translation key under `ftu.checklist.items.<id>`. */
  key: string;
}

const DEFAULT_ITEMS: ChecklistDef[] = [
  {
    id: 'connect-account',
    prefix: '/settings/accounts',
    to: '/settings/accounts',
    key: 'connectAccount',
  },
  { id: 'edit-voice', prefix: '/settings/voice', to: '/settings/voice', key: 'editVoice' },
  { id: 'first-business', prefix: '/businesses', to: '/businesses', key: 'firstBusiness' },
  { id: 'first-campaign', prefix: '/campaigns', to: '/campaigns', key: 'firstCampaign' },
  { id: 'explore-inbox', prefix: '/inbox', to: '/inbox', key: 'exploreInbox' },
  { id: 'demo-data', prefix: '__demo__', to: '/__demo__', key: 'demoData' },
];

export interface UseFtuChecklistOptions {
  /** Override the default checklist (testing). */
  items?: ChecklistDef[];
}

export interface UseFtuChecklistReturn {
  items: FtuChecklistItem[];
  total: number;
  done: number;
  /** Add a visited-page prefix for the current user (debounced server-side). */
  track: (prefix: string) => void;
  /** Find the route a clicked checklist item navigates to. */
  resolveTarget: (id: string) => string | undefined;
}

export function useFtuChecklist(options: UseFtuChecklistOptions = {}): UseFtuChecklistReturn {
  const t = useT('ftu');
  const me = useMe();
  const tracker = useTrackVisitedPages();
  const itemsDef = options.items ?? DEFAULT_ITEMS;

  // The "Connect a LinkedIn account" item is a real-completion action, not a
  // page-visit tour step: it must tick once the user actually has a connected
  // account — even if they connected from the extension popup and never opened
  // `/settings/accounts` in the web app. (`GET /linkedin-accounts` already
  // lists only non-archived accounts.) Without this the checklist showed the
  // account as unconnected despite a paired, ingesting account.
  const linkedinAccounts = useLinkedinAccounts();
  const hasConnectedAccount = (linkedinAccounts.data?.accounts?.length ?? 0) > 0;

  const visited = useMemo<string[]>(() => {
    const raw = (me.data?.user as { visitedPages?: unknown } | undefined)?.visitedPages;
    return Array.isArray(raw) ? (raw as string[]) : [];
  }, [me.data]);

  const visitedSet = useMemo(() => new Set(visited), [visited]);

  const items = useMemo<FtuChecklistItem[]>(
    () =>
      itemsDef.map((def) => ({
        id: def.id,
        title: t(`checklist.items.${def.key}.title`),
        sub: t(`checklist.items.${def.key}.sub`),
        done: visitedSet.has(def.prefix) || (def.id === 'connect-account' && hasConnectedAccount),
      })),
    [itemsDef, t, visitedSet, hasConnectedAccount],
  );

  const track = useCallback(
    (prefix: string) => {
      if (visitedSet.has(prefix)) return;
      tracker.mutate({ pages: [prefix] });
    },
    [tracker, visitedSet],
  );

  const resolveTarget = useCallback(
    (id: string) => itemsDef.find((d) => d.id === id)?.to,
    [itemsDef],
  );

  const done = items.filter((i) => i.done).length;

  return { items, total: items.length, done, track, resolveTarget };
}
